import React, { FC, useCallback, useMemo } from "react"
import { Button } from "dash-styled"
import { useSelection } from "../../../../../../Hooks/useSelection"
import { getValue } from "../../../../../../Models/Client/FetchState"
import { SortExpression } from "../../../../../../Models/Client/SortExpression"
import { SortChooserProps } from "../../../../../Common/Button/SortChooser"
import { Confirm } from "../../../../../Common/Modal/Confirm"
import { Table } from "resha"
import { TableHeader } from "../../../../../Common/Table/TableHeader"
import { defaultWorkerGroupSort } from "../../Worker/Common/constants"
import { AppDetailBase } from "../AppDetailBase/AppDetailBase"
import { AssignModal } from "./Common/AssignModal"
import { AppWorkerRole } from "../../../../../../Models/API/AppWorkerRole"
import {
    appWorkerGroupColumns,
    appWorkerGroupSortOptions
} from "./Common/constants"
import { AppWorkerRoleChangeModal } from "./Common/AppWorkerRoleChangeModal"
import {
    APIAppWorkerGroup,
    getAppWorkerGroupId
} from "../../../../../../Models/API/AppWorkerGroup"
import { useAppWorkerGroupList } from "../../../../../../Hooks/useAppWorkerGroupList"
import {
    assignWorkerGroupToApp,
    unassignWorkerGroupFromApp,
    updateWorkerGroupInApp
} from "../../../../../../Actions/API/WorkerGroupActions"
import {
    APIWorkerGroup,
    getNumericWorkerGroupId,
    toDisplayText
} from "../../../../../../Models/API/WorkerGroup"
import { useWorkerGroupList } from "../../../../../../Hooks/useWorkerGroupList"
import { routePaths } from "../../../../../../Routes"
import { useModal } from "../../../../../../Hooks/useModal"
import { useDispatch } from "react-redux"
import { push } from "connected-react-router"
import { ReactComponent as ExternalLinkIcon } from "icons/lib/external-link.svg"
import { useAssumedRoles } from "../../../../../../Hooks/useAssumedRoles"
import { hasAppPermission } from "../../../../../../Models/Client/ProjectRole"

type Modal =
    | { type: "add" }
    | { type: "change-role"; group: APIAppWorkerGroup }
    | { type: "remove"; group: APIAppWorkerGroup }

const rowActions = ["Change role", "Remove from project"] as const

export const AppWorkerGroupList: FC<{
    orgUsername: string
    appID: string
    sort: SortExpression<APIAppWorkerGroup>
}> = ({ orgUsername, appID, sort }) => {
    const dispatch = useDispatch()
    const rolesFetch = useAssumedRoles(appID)
    const [appWorkerGroupsFetch, loadMore] = useAppWorkerGroupList(appID, sort)
    const { modal, isSaving, open, close, save } = useModal<Modal>()
    const sortOptions = useMemo<SortChooserProps<APIAppWorkerGroup>>(
        () => ({
            fields: appWorkerGroupSortOptions,
            applied: sort,
            onChange: newSort =>
                dispatch(
                    push(
                        routePaths.appWorkerGroupList(
                            orgUsername,
                            appID,
                            newSort
                        )
                    )
                )
        }),
        [appID, dispatch, orgUsername, sort]
    )
    const [workerGroupsFetch, loadMoreWorkerGroups, reloadWorkerGroups] =
        useWorkerGroupList(orgUsername, defaultWorkerGroupSort)
    const handleRowAction = useCallback(
        (selected: typeof rowActions[number], id: number) => {
            const group = getValue(appWorkerGroupsFetch)?.items.find(
                worker => worker.workerGroupId === id
            )

            if (group === undefined) {
                return
            }

            switch (selected) {
                case "Remove from project":
                    open({
                        type: "remove",
                        group
                    })
                    return
                case "Change role":
                    open({
                        type: "change-role",
                        group
                    })
            }
        },
        [appWorkerGroupsFetch, open]
    )
    const columns = useMemo(
        () => appWorkerGroupColumns([...rowActions], handleRowAction),
        [handleRowAction]
    )
    const handleRemove = useCallback(() => {
        if (modal?.type === "remove") {
            save(unassignWorkerGroupFromApp(appID, modal.group.workerGroupId))
        }
    }, [appID, modal, save])
    const handleAdd = useCallback(() => open({ type: "add" }), [open])
    const handleAssign = useCallback(
        (ids: number[], role: AppWorkerRole) => {
            if (modal?.type === "add") {
                save(assignWorkerGroupToApp(appID, ids, role))
            }
        },
        [appID, modal, save]
    )
    const handleSaveRole = useCallback(
        (role: AppWorkerRole) => {
            if (modal?.type === "change-role") {
                save(updateWorkerGroupInApp({ ...modal.group, role }))
            }
        },
        [modal, save]
    )
    const count = getValue(appWorkerGroupsFetch)?.total
    const [selected, toggleSelection] = useSelection(
        getAppWorkerGroupId,
        getValue(appWorkerGroupsFetch)?.items ?? []
    )
    const selectedIds = useMemo(
        () => selected.map(getAppWorkerGroupId),
        [selected]
    )
    // TODO: this predicate only checks already fetched app workers.
    // We need endpoint to return workers that aren't assigned to this app.
    const isAlreadyAssigned = useCallback(
        (group: APIWorkerGroup) =>
            getValue(appWorkerGroupsFetch)?.items.some(
                w => w.workerGroupId === group.id
            ) ?? false,
        [appWorkerGroupsFetch]
    )

    return (
        <AppDetailBase
            id={appID}
            orgUsername={orgUsername}
            title="Worker Groups"
            breadcrumbs={["Workers"]}
            fixedContentWidth={false}
        >
            {hasAppPermission(getValue(rolesFetch) ?? [], "workers:invite") && (
                <Button
                    className="w-48"
                    variant="contained"
                    onClick={handleAdd}
                >
                    Add worker group
                </Button>
            )}
            <TableHeader
                filteredCount={count}
                totalCount={count}
                sortOptions={sortOptions}
            />
            <div className="flex-grow h-0 flex-basis-0 overflow-y-auto -m-6 mt-0 px-6">
                <Table
                    className="w-full"
                    columns={columns}
                    getKey={getAppWorkerGroupId}
                    items={getValue(appWorkerGroupsFetch)?.items}
                    itemCount={count}
                    stickyHeader={true}
                    loadMore={loadMore}
                    checkedItemIds={selectedIds}
                    onToggleRowCheck={toggleSelection}
                />
            </div>
            {modal?.type === "add" && (
                <AssignModal
                    resource="worker group"
                    isSaving={isSaving}
                    models={getValue(workerGroupsFetch)?.items ?? []}
                    isAlreadyAssigned={isAlreadyAssigned}
                    renderModel={toDisplayText}
                    createLink={
                        <a
                            className="inline-block text-pink-500 no-underline hover:underline mt-1"
                            target="_blank"
                            rel="noopener noreferrer"
                            href={routePaths.orgWorkerGroupAdd(orgUsername)}
                        >
                            Create new worker group
                            <ExternalLinkIcon className="inline-block ml-1 w-4 h-4 align-text-bottom" />
                        </a>
                    }
                    getId={getNumericWorkerGroupId}
                    onClose={close}
                    onSubmit={handleAssign}
                    onLoadMore={loadMoreWorkerGroups}
                    onReload={reloadWorkerGroups}
                />
            )}
            {modal?.type === "change-role" && (
                <AppWorkerRoleChangeModal
                    title="Change worker group role"
                    currentRole={modal.group.role}
                    isSaving={isSaving}
                    onClose={close}
                    onSubmit={handleSaveRole}
                />
            )}
            {modal?.type === "remove" && (
                <Confirm
                    title={`Remove ${modal.group.workerGroupName}?`}
                    yesLabel={isSaving ? "Removing..." : "Remove"}
                    noLabel="Cancel"
                    isProcessing={isSaving}
                    onClose={close}
                    onClickYes={handleRemove}
                    onClickNo={close}
                />
            )}
        </AppDetailBase>
    )
}
